import {
  UserCommunication,
  UserCommunicationAttachment,
} from '@this+that/models';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { createUserCommunicationsSlice } from './actions';
import { mergeHydratedUserCommunicationsState } from './mergeHydratedState';
import { UserCommunicationsStore } from './types';
import { ZustandStores } from '../../utils';
import { createIndexedDBStorage } from '../../utils/indexedDBAdapter';

const removeContentFromAttachments = (state: UserCommunicationsStore) => {
  const cleanAttachmentsRecursively = (
    communication: UserCommunication,
  ): UserCommunication => ({
    ...communication,
    attachments: communication.attachments?.map(
      (attachment: UserCommunicationAttachment) => ({
        ...attachment,
        content: undefined as any,
      }),
    ),
    threadMessages: communication.threadMessages?.map(
      (threadMessage: UserCommunication) =>
        cleanAttachmentsRecursively(threadMessage),
    ),
  });

  const cleanCommunications = (communications: typeof state.all) =>
    communications.map((communication) =>
      cleanAttachmentsRecursively(communication),
    );

  return {
    ...state,
    primary: cleanCommunications(state.primary),
    promotions: cleanCommunications(state.promotions),
    social: cleanCommunications(state.social),
    updates: cleanCommunications(state.updates),
    forums: cleanCommunications(state.forums),
    draft: cleanCommunications(state.draft),
    sent: cleanCommunications(state.sent),
    spam: cleanCommunications(state.spam),
    trash: cleanCommunications(state.trash),
    labels: Object.fromEntries(
      Object.entries(state.labels).map(([key, communications]) => [
        key,
        cleanCommunications(communications),
      ]),
    ),
    all: cleanCommunications(state.all),
  };
};

// v1 dropped draftAbsorbedMessageIds — the "insert as a separate row and hide
// the original" mechanism was replaced by the draft becoming the thread's own
// head row in place, so the field no longer exists on the store shape.
export const USER_COMMUNICATIONS_PERSIST_VERSION = 1;

export const migrateUserCommunicationsPersistedState = (
  persistedState: unknown,
  version: number,
): Partial<UserCommunicationsStore> => {
  if (version < USER_COMMUNICATIONS_PERSIST_VERSION) {
    const { draftAbsorbedMessageIds: _dropped, ...rest } = (persistedState ??
      {}) as Partial<UserCommunicationsStore> & {
      draftAbsorbedMessageIds?: string[];
    };
    return rest;
  }
  return persistedState as Partial<UserCommunicationsStore>;
};

export const useUserCommunicationsStore = create<UserCommunicationsStore>()(
  persist(
    (set, get, store) => ({
      ...createUserCommunicationsSlice(set, get, store),
    }),
    {
      name: ZustandStores.USER_COMMUNICATIONS,
      version: USER_COMMUNICATIONS_PERSIST_VERSION,
      storage: createJSONStorage(() => createIndexedDBStorage()),
      partialize: (state) => {
        // Exclude recentlyInsertedIds (transient animation state), isLoading (in-flight flag that must never hydrate as true) and syncedViewTypes (must start empty on every page load) and fetchGeneration (in-memory staleness counter) from persistence.
        const {
          recentlyInsertedIds: _recentlyInsertedIds,
          syncedViewTypes: _syncedViewTypes,
          fetchGeneration: _fetchGeneration,
          isLoading: _isLoading,
          ...rest
        } = removeContentFromAttachments(state);
        return rest;
      },
      migrate: migrateUserCommunicationsPersistedState,
      merge: (persisted, current) =>
        mergeHydratedUserCommunicationsState(persisted, current),
    },
  ),
);
